"use client"

import { useEffect, useRef } from "react"
import { captureChatMessage } from "@/lib/sentry-chat"
import type { ChatAttachment } from "./use-chat-session"

export interface IncomingMessageEvent {
  type: "message"
  conversationId: number
  messageId: number
  content: string
  senderType: "contact" | "agent_bot" | "user" | "agent"
  senderName?: string
  /** The agent's Chatwoot profile photo, when they've set one. */
  senderAvatarUrl?: string
  timestamp: string
  private: boolean
  /** Files/images on the Chatwoot message, if any (AHD-7851). */
  attachments?: ChatAttachment[]
}

/** Raw Chatwoot message attachment, as it appears on the REST + cable payloads. */
interface RawChatwootAttachment {
  id?: number
  file_type?: string
  data_url?: string
  file_size?: number
}

/** Map Chatwoot's attachment shape onto our ChatAttachment. Returns undefined
 *  when there's nothing to map, so a turn without attachments stays clean. */
function mapChatwootAttachments(
  raw: RawChatwootAttachment[] | undefined
): ChatAttachment[] | undefined {
  if (!raw?.length) return undefined
  const mapped = raw
    .filter((a): a is RawChatwootAttachment & { data_url: string } =>
      Boolean(a.data_url)
    )
    .map((a) => ({
      id: String(a.id ?? a.data_url),
      kind: a.file_type === "image" ? ("image" as const) : ("file" as const),
      url: a.data_url,
      mime: a.file_type,
      sizeBytes: a.file_size,
    }))
  return mapped.length ? mapped : undefined
}

export interface ConversationUpdatedEvent {
  type: "conversation_updated"
  conversationId: number
  customAttributes?: Record<string, unknown>
}

/** An agent's typing status, from Chatwoot's `conversation.typing_on` /
 *  `conversation.typing_off` frames on the RoomChannel (AHD-9457). */
export interface AgentTypingEvent {
  type: "typing"
  conversationId: number
  typing: boolean
}

export type ChatwootEvent =
  | IncomingMessageEvent
  | ConversationUpdatedEvent
  | AgentTypingEvent

interface UseChatwootSubscriptionArgs {
  chatwootBaseUrl: string // wss URL base, e.g. "wss://chat.aheadhealth.dev"
  pubsubToken: string | null // null → don't subscribe
  sourceId: string | null
  conversationId: number | null
  onEvent: (e: ChatwootEvent) => void
  onStatusChange?: (
    status: "connecting" | "connected" | "polling" | "disconnected"
  ) => void
}

/**
 * Subscribes to the contact's ActionCable RoomChannel and delivers events.
 * Falls back to 5s polling of /api/chat/messages if the WS fails.
 *
 * Idempotent: effect reconnects on pubsubToken change. One subscription per session.
 */
export function useChatwootSubscription(
  args: UseChatwootSubscriptionArgs
): void {
  const {
    chatwootBaseUrl,
    pubsubToken,
    sourceId,
    conversationId,
    onEvent,
    onStatusChange,
  } = args
  const wsRef = useRef<WebSocket | null>(null)
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastSeenMessageId = useRef<number | null>(null)
  // Capture each WS failure mode at most once per connection so a flapping
  // socket doesn't flood Sentry; reset on each successful (re)connect.
  const reportedWsFailure = useRef(false)
  // Reconnect bookkeeping. A dropped socket retries with capped exponential
  // backoff rather than degrading to polling forever — polling only bridges
  // while the socket is down and is torn down once it reconnects.
  const reconnectAttempts = useRef(0)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closedByCleanup = useRef(false)

  useEffect(() => {
    if (!pubsubToken) return
    // Fresh subscription: clear any state left from a prior deps cycle.
    closedByCleanup.current = false
    reconnectAttempts.current = 0
    reportedWsFailure.current = false
    onStatusChange?.("connecting")

    const RECONNECT_BASE_MS = 1000
    const RECONNECT_MAX_MS = 30000

    function stopPolling() {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current)
        pollTimerRef.current = null
      }
    }

    function startPolling() {
      // Guard against stacking intervals if multiple closes fire.
      if (!sourceId || pollTimerRef.current) return
      onStatusChange?.("polling")
      pollTimerRef.current = setInterval(async () => {
        try {
          const url = new URL("/api/chat/messages", window.location.origin)
          if (lastSeenMessageId.current !== null) {
            url.searchParams.set("after", String(lastSeenMessageId.current))
          }
          if (conversationId) {
            url.searchParams.set("conversation_id", String(conversationId))
          }
          const res = await fetch(url.toString(), {
            headers: { "X-Auth-Token": sourceId },
          })
          if (!res.ok) return
          const data = (await res.json()) as {
            messages: Array<{
              id: number
              content: string
              message_type: number
              conversation_id: number
              created_at: number
              private?: boolean
              sender?: { type: string; name?: string; avatar_url?: string }
              attachments?: RawChatwootAttachment[]
            }>
          }
          for (const m of data.messages) {
            if (
              lastSeenMessageId.current !== null &&
              m.id <= lastSeenMessageId.current
            )
              continue
            if (m.private) continue
            lastSeenMessageId.current = m.id
            onEvent({
              type: "message",
              conversationId: m.conversation_id,
              messageId: m.id,
              content: m.content,
              senderType: (m.sender?.type as "agent") ?? "agent",
              senderName: m.sender?.name,
              senderAvatarUrl: m.sender?.avatar_url,
              timestamp: String(m.created_at),
              private: false,
              attachments: mapChatwootAttachments(m.attachments),
            })
          }
        } catch {
          // swallow — the next tick will retry
        }
      }, 5000)
    }

    function scheduleReconnect() {
      if (closedByCleanup.current || reconnectTimerRef.current) return
      const delay = Math.min(
        RECONNECT_BASE_MS * 2 ** reconnectAttempts.current,
        RECONNECT_MAX_MS
      )
      reconnectAttempts.current += 1
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null
        connectWs()
      }, delay)
    }

    function connectWs() {
      if (closedByCleanup.current) return
      try {
        const ws = new WebSocket(`${chatwootBaseUrl}/cable`)
        wsRef.current = ws

        ws.onopen = () => {
          // Live again — reset backoff and tear down the polling bridge.
          reconnectAttempts.current = 0
          reportedWsFailure.current = false
          stopPolling()
          onStatusChange?.("connected")
          ws.send(
            JSON.stringify({
              command: "subscribe",
              identifier: JSON.stringify({
                channel: "RoomChannel",
                pubsub_token: pubsubToken,
              }),
            })
          )
        }
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data as string)
            if (msg.type === "welcome" || msg.type === "ping") return
            if (msg.type === "confirm_subscription") return
            if (msg.message) {
              const m = msg.message as {
                event?: string
                data?: {
                  id?: number
                  content?: string
                  message_type?: number
                  conversation_id?: number
                  created_at?: string
                  private?: boolean
                  sender?: { type: string; name?: string; avatar_url?: string }
                  custom_attributes?: Record<string, unknown>
                  attachments?: RawChatwootAttachment[]
                  // Typing frames carry the conversation as a nested object
                  // rather than a flat conversation_id.
                  conversation?: { id?: number }
                }
              }
              if (m.event === "message.created" && m.data) {
                if (m.data.private) return
                if (m.data.message_type !== 1) return
                // No id, no event: consumers dedup redeliveries by message id,
                // so a defaulted id (two id-less frames both mapping to 0)
                // would silently swallow the second one. Chatwoot always sends
                // ids in practice — this is the safe shape of "defensive".
                if (typeof m.data.id !== "number") return
                onEvent({
                  type: "message",
                  conversationId: m.data.conversation_id ?? 0,
                  messageId: m.data.id,
                  content: m.data.content ?? "",
                  senderType: (m.data.sender?.type as "agent") ?? "agent",
                  senderName: m.data.sender?.name,
                  senderAvatarUrl: m.data.sender?.avatar_url,
                  timestamp: m.data.created_at ?? new Date().toISOString(),
                  private: false,
                  attachments: mapChatwootAttachments(m.data.attachments),
                })
              } else if (m.event === "conversation.updated" && m.data) {
                onEvent({
                  type: "conversation_updated",
                  conversationId: m.data.conversation_id ?? 0,
                  customAttributes: m.data.custom_attributes,
                })
              } else if (
                (m.event === "conversation.typing_on" ||
                  m.event === "conversation.typing_off") &&
                m.data
              ) {
                onEvent({
                  type: "typing",
                  conversationId:
                    m.data.conversation_id ?? m.data.conversation?.id ?? 0,
                  typing: m.event === "conversation.typing_on",
                })
              }
            }
          } catch {
            // Ignore malformed frames.
          }
        }
        ws.onerror = () => {
          // The browser's WS error event carries no close code, and a `close`
          // always follows it — so reporting here just fires an uninformative
          // warning for benign disconnects (mobile sleep, network switch) that
          // onclose deliberately filters out. Bridge to polling + reconnect and
          // let onclose own the code-aware Sentry decision.
          startPolling()
          scheduleReconnect()
        }
        ws.onclose = (ev) => {
          // Intentional close on unmount/deps-change — don't reconnect or poll.
          if (closedByCleanup.current) return
          onStatusChange?.("disconnected")
          // Benign close codes that don't warrant a Sentry report:
          // 1000 — normal close · 1001 — Going Away · 1005 — No Status ·
          // 1006 — Abnormal Closure (network switch, mobile sleep, etc.) ·
          // 1015 — TLS Handshake (browser-synthesized like 1005/1006, a
          // transient client-side TLS/network blip — never sent by the server)
          const benign =
            ev.code === 1000 ||
            ev.code === 1001 ||
            ev.code === 1005 ||
            ev.code === 1006 ||
            ev.code === 1015
          if (!benign && !reportedWsFailure.current) {
            reportedWsFailure.current = true
            captureChatMessage(
              "Chatwoot WebSocket closed unexpectedly",
              "warning",
              {
                conversation_id: conversationId,
                extra: { kind: "ws_close", code: ev.code, reason: ev.reason },
              }
            )
          }
          // Bridge with polling and retry the socket with backoff.
          startPolling()
          scheduleReconnect()
        }
      } catch (err) {
        captureChatMessage("Chatwoot WebSocket constructor threw", "warning", {
          conversation_id: conversationId,
          extra: {
            kind: "ws_construct",
            error: err instanceof Error ? err.message : String(err),
          },
        })
        startPolling()
        scheduleReconnect()
      }
    }

    // Network came back — retry the socket immediately instead of waiting out
    // the backoff (and don't double-connect a live one).
    function handleOnline() {
      const ws = wsRef.current
      if (
        ws &&
        (ws.readyState === WebSocket.OPEN ||
          ws.readyState === WebSocket.CONNECTING)
      )
        return
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
      reconnectAttempts.current = 0
      connectWs()
    }

    connectWs()
    window.addEventListener("online", handleOnline)
    return () => {
      closedByCleanup.current = true
      window.removeEventListener("online", handleOnline)
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
      wsRef.current?.close()
      wsRef.current = null
      stopPolling()
    }
  }, [
    chatwootBaseUrl,
    pubsubToken,
    sourceId,
    conversationId,
    onEvent,
    onStatusChange,
  ])
}
